import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getArtformContent } from '../utils/artformService';
import { buildAccordionSections } from '../utils/artformDetailSections';

function SectionBody({ section }) {
  if (section.type === 'compound' && section.parts?.length) {
    return (
      <div className="space-y-4 text-sm md:text-base text-amber-900 leading-relaxed">
        {section.parts.map((p) => (
          <div key={p.h}>
            <p className="font-semibold text-amber-800 mb-1">{p.h}</p>
            <p className="whitespace-pre-wrap">{p.t}</p>
          </div>
        ))}
      </div>
    );
  }
  if (section.type === 'text' && section.body) {
    return (
      <p className="text-sm md:text-base text-amber-900 leading-relaxed whitespace-pre-wrap">
        {section.body}
      </p>
    );
  }
  if (section.type === 'list' && section.items?.length) {
    return (
      <ul className="list-disc list-inside space-y-2 text-sm md:text-base text-amber-900">
        {section.items.map((item, idx) => (
          <li key={idx} className="leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    );
  }
  if (section.type === 'works' && section.works?.length) {
    return (
      <div className="space-y-3">
        {section.works.map((work, idx) => (
          <div
            key={idx}
            className="bg-white/90 rounded-lg p-3 border border-amber-200/90 shadow-sm"
          >
            <p className="font-semibold text-amber-900 text-sm md:text-base">
              {work.title || 'Untitled'}
            </p>
            {work.artist && (
              <p className="text-amber-700 text-xs md:text-sm mt-1">by {work.artist}</p>
            )}
          </div>
        ))}
      </div>
    );
  }
  if (section.type === 'chips' && section.items?.length) {
    return (
      <div className="flex flex-wrap gap-2">
        {section.items.map((item, idx) => (
          <span
            key={idx}
            className="bg-amber-100 text-amber-900 px-3 py-1 rounded-full text-xs md:text-sm font-medium border border-amber-200"
          >
            {item}
          </span>
        ))}
      </div>
    );
  }
  return null;
}

const flipVariants = {
  enter: (direction) => ({
    rotateY: direction >= 0 ? 88 : -88,
    opacity: 0,
    scale: 0.94,
    x: direction >= 0 ? 12 : -12,
  }),
  center: {
    rotateY: 0,
    opacity: 1,
    scale: 1,
    x: 0,
  },
  exit: (direction) => ({
    rotateY: direction >= 0 ? -88 : 88,
    opacity: 0,
    scale: 0.94,
    x: direction >= 0 ? -12 : 12,
  }),
};

const fadeVariants = {
  enter: { opacity: 0, y: 8 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

/**
 * Artform sections as a flip deck with Next / Back (replaces accordion dropdowns).
 */
const ArtformDetailAccordions = ({ artformKey, styleMeta = {}, className = '' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState([]);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!artformKey) {
        setSections(buildAccordionSections(null, styleMeta));
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await getArtformContent(artformKey);
        if (cancelled) return;
        setSections(buildAccordionSections(data, styleMeta));
      } catch (e) {
        console.error('[ArtformDetailAccordions]', e);
        if (!cancelled) {
          setError('Could not load shared style facts. Showing what we know from this activity.');
          setSections(buildAccordionSections(null, styleMeta));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [artformKey, styleMeta?.name, styleMeta?.description, styleMeta?.kidFriendlyDescription, styleMeta?.yearRange]);

  useEffect(() => {
    setIndex(0);
    setDirection(0);
  }, [sections]);

  const total = sections.length;
  const current = sections[index];
  const atStart = index <= 0;
  const atEnd = index >= total - 1;
  const reduceMotion = useReducedMotion();
  const variants = reduceMotion ? fadeVariants : flipVariants;
  const transition = reduceMotion
    ? { duration: 0.2, ease: 'easeOut' }
    : { duration: 0.38, ease: [0.22, 1, 0.36, 1] };

  const goNext = () => {
    if (atEnd) return;
    setDirection(1);
    setIndex((i) => i + 1);
  };

  const goBack = () => {
    if (atStart) return;
    setDirection(-1);
    setIndex((i) => i - 1);
  };

  if (loading) {
    return (
      <div className={`space-y-3 min-w-0 ${className}`}>
        <div className="h-6 bg-amber-100/80 rounded w-2/3 animate-pulse" />
        <div className="h-48 bg-amber-50 rounded-xl border border-amber-100 animate-pulse" />
        <div className="h-10 bg-amber-100/60 rounded-full animate-pulse" />
      </div>
    );
  }

  return (
    <div className={`min-w-0 max-w-full w-full box-border ${className}`}>
      {error && (
        <p className="text-xs text-amber-700 mb-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      {total > 0 && current && (
        <div className="min-w-0">
          <div
            className="flex flex-col rounded-xl border border-amber-200/90 bg-gradient-to-br from-amber-50/95 to-orange-50/80 shadow-inner overflow-hidden min-h-[280px] md:min-h-[320px]"
            style={reduceMotion ? undefined : { perspective: 1100 }}
          >
            <div className="shrink-0 flex items-center justify-between gap-2 px-4 pt-3 pb-2 md:px-6 md:pt-4 md:pb-2.5 text-xs text-amber-700/90 font-medium">
              <span>
                Card {index + 1} of {total}
              </span>
              <div className="flex gap-1" aria-hidden>
                {sections.map((s, i) => (
                  <span
                    key={`${s.id}-${i}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === index ? 'w-6 bg-amber-600' : 'w-1.5 bg-amber-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="relative flex-1 min-h-0 min-w-0">
              <AnimatePresence initial={false} mode="wait" custom={direction}>
                <motion.div
                  key={`${index}-${current.id}`}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={transition}
                  className="absolute inset-0 overflow-y-auto overflow-x-hidden min-w-0 px-4 pb-4 pt-0 md:px-6 md:pb-6 md:pt-0"
                  style={{ transformStyle: 'preserve-3d', backfaceVisibility: 'hidden' }}
                >
                  <h4 className="text-lg md:text-xl font-semibold text-amber-900 leading-snug px-0.5 pt-1 pb-3.5 md:pb-4">
                    {current.title}
                  </h4>
                  <div className="min-w-0 max-w-full">
                    <SectionBody section={current} />
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goBack();
              }}
              disabled={atStart}
              className="inline-flex items-center justify-center rounded-full border-2 border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 shadow-sm transition-all hover:bg-amber-50 disabled:opacity-40 disabled:pointer-events-none min-w-[5.5rem]"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              disabled={atEnd}
              className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-600 to-orange-600 px-5 py-2 text-sm font-semibold text-white shadow-md transition-all hover:from-amber-700 hover:to-orange-700 disabled:opacity-40 disabled:pointer-events-none min-w-[5.5rem]"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtformDetailAccordions;
